import React, { useEffect, useState } from 'react'
import { Select as SutraSelect } from 'sutra/components/Select/Select'

import {
  NumericOperatorSelectWrap,
  NumericValueInput,
  PaneContainer,
  PaneHeading,
  PaneRow,
} from 'features/charts-v2/components/filter-popover-pane.styles'
import { FilterPopoverPaneFooter } from 'features/charts-v2/components/FilterPopoverPaneFooter/FilterPopoverPaneFooter'
import {
  NUMERIC_OPERATOR_OPTIONS,
  type NumericOperatorType,
} from 'features/charts-v2/types'

interface FilterPopoverNumericPaneProps {
  field: 'record_count' | 'sentence_count'
  label: string
  // Currently-committed filter, or null when no filter is applied for this field.
  operator: NumericOperatorType | null
  value: number | undefined
  onApply: (operator: NumericOperatorType, value: number) => void
  onClear: () => void
}

// In-popover editor for the Records / Sentences numeric filters. Unlike the
// sidebar's NumericFilterCard (which debounces every keystroke straight to
// the panel), nothing here writes until Apply is clicked — Apply is the only
// commit point, so the popover can no longer create a placeholder "0" card.
export const FilterPopoverNumericPane: React.FC<
  FilterPopoverNumericPaneProps
> = ({ label, operator, value, onApply, onClear }) => {
  const [draftOperator, setDraftOperator] = useState<NumericOperatorType>(
    operator ?? 'greater than',
  )
  const [draftValue, setDraftValue] = useState<number | undefined>(value)

  // Re-sync the draft when the committed filter changes externally (Apply
  // round-tripping through the panel, or re-opening the pane on an already
  // -applied filter).
  useEffect(() => {
    setDraftOperator(operator ?? 'greater than')
    setDraftValue(value)
  }, [operator, value])

  const hasCommittedFilter = value !== undefined

  return (
    <PaneContainer>
      <PaneHeading>{label}</PaneHeading>
      <PaneRow>
        <NumericOperatorSelectWrap>
          <SutraSelect
            value={draftOperator}
            onChange={(picked) => setDraftOperator(picked)}
            options={NUMERIC_OPERATOR_OPTIONS}
            zIndex={2001}
          />
        </NumericOperatorSelectWrap>
        <NumericValueInput
          type="number"
          $height="30px"
          value={draftValue ?? ''}
          onChange={(e) =>
            setDraftValue(
              e.target.value === '' ? undefined : Number(e.target.value),
            )
          }
          min={0}
          placeholder="e.g. 5"
        />
      </PaneRow>
      <FilterPopoverPaneFooter
        applyDisabled={draftValue === undefined || Number.isNaN(draftValue)}
        clearDisabled={!hasCommittedFilter}
        onApply={() => {
          if (draftValue === undefined || Number.isNaN(draftValue)) return
          onApply(draftOperator, draftValue)
        }}
        onClear={onClear}
      />
    </PaneContainer>
  )
}
